
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { BookingInfo } from "@/types/booking";
import { createBookingRecord } from "@/utils/booking-utils";
import { createCheckoutSession, cleanupFailedBooking } from "@/services/payment-service";
import { STRIPE_PUBLIC_KEY } from "@/config/stripe";
import { supabase } from "@/integrations/supabase/client";

export const usePaymentSession = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, session } = useAuth();

  const handlePaymentSession = async (
    bookingInfo: BookingInfo,
    selectedDate: Date,
    selectedTime: string,
    discount: number = 0,
    couponId: string | null = null
  ) => {
    if (!bookingInfo) {
      toast({
        variant: "destructive",
        title: "Missing Booking Information",
        description: "Unable to proceed with payment. Booking details are missing."
      });
      return;
    }
    
    // Check for valid authentication before proceeding
    if (!user || !session) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please log in to complete your booking."
      });
      navigate('/client-auth', { state: { returnTo: '/payment' } });
      return;
    }
    
    // Validate required booking information
    if (!bookingInfo.serviceId || !bookingInfo.practitionerId) {
      console.error("Missing critical booking IDs:", {
        serviceId: bookingInfo.serviceId,
        practitionerId: bookingInfo.practitionerId
      });
      
      toast({
        variant: "destructive",
        title: "Invalid Booking Details",
        description: "Critical booking information is missing. Please start over."
      });
      
      navigate('/book-an-appointment');
      return;
    }
    
    setIsLoading(true);
    let confirmedBookingId: string | null = null;

    try {
      // Step 1: Create booking record
      try {
        // Extract bookingId from bookingInfo if available
        const preGeneratedBookingId = bookingInfo.bookingId;
        confirmedBookingId = await createBookingRecord(bookingInfo, selectedDate, selectedTime, user.id, preGeneratedBookingId);
        console.log("Booking record created successfully:", confirmedBookingId);
      } catch (bookingError: any) {
        console.error('Booking creation error:', bookingError);
        toast({
          variant: "destructive",
          title: "Booking Failed",
          description: bookingError.message || "There was a problem creating your booking. Please try again."
        });
        setIsLoading(false);
        return;
      }
      
      if (!confirmedBookingId) {
        throw new Error("Failed to create booking record");
      }
      
      // Step 2: Collect geographic data before checkout
      try {
        console.log("Collecting geographic data for user:", user.id);
        await supabase.functions.invoke('collect-geo-data', {
          body: {
            clientId: user.id,
            bookingId: confirmedBookingId
          }
        });
      } catch (geoError) {
        console.log("Geographic data collection failed (non-critical):", geoError);
        // This is non-critical, so we continue with the payment process
      }

      // Step 3: Create checkout session with enhanced error handling
      try {
        console.log("Creating checkout session for booking:", confirmedBookingId);
        const data = await createCheckoutSession(
          confirmedBookingId, 
          bookingInfo, 
          discount, 
          couponId
        );
        console.log("Checkout session created successfully:", data);
        
        // Step 4: Direct to Stripe checkout
        if (data.url || data.checkoutUrl) {
          const checkoutUrl = data.url || data.checkoutUrl;
          console.log("Redirecting to Stripe checkout URL:", checkoutUrl);
          
          // Enhanced session preservation for paid bookings
          if (user && session) {
            const sessionInfo = {
              user_id: user.id,
              booking_id: confirmedBookingId,
              user_email: user.email || '',
              timestamp: Date.now(),
              session_token: session.access_token,
              refresh_token: session.refresh_token,
              expires_at: session.expires_at
            };
            
            // Store in both localStorage and sessionStorage for better persistence
            Object.entries(sessionInfo).forEach(([key, value]) => {
              localStorage.setItem(`payment_session_${key}`, String(value));
              sessionStorage.setItem(`payment_session_${key}`, String(value));
            });
            
            console.log('Enhanced session info preserved before payment redirect');
          }
          
          window.location.href = checkoutUrl;
          return;
        }
        
        // Fallback to Stripe JS library
        if (!window.Stripe) {
          console.error("Stripe.js not loaded");
          throw new Error("Payment system not loaded. Please refresh the page and try again.");
        }
        
        const stripe = window.Stripe(STRIPE_PUBLIC_KEY);
        console.log("Redirecting to Stripe checkout with session ID:", data.sessionId);
        
        // Enhanced session preservation for paid bookings
        if (user && session) {
          const sessionInfo = {
            user_id: user.id,
            booking_id: confirmedBookingId,
            user_email: user.email || '',
            timestamp: Date.now(),
            session_token: session.access_token,
            refresh_token: session.refresh_token,
            expires_at: session.expires_at
          };
          
          // Store in both localStorage and sessionStorage for better persistence
          Object.entries(sessionInfo).forEach(([key, value]) => {
            localStorage.setItem(`payment_session_${key}`, String(value));
            sessionStorage.setItem(`payment_session_${key}`, String(value));
          });
          
          console.log('Enhanced session info preserved before payment redirect');
        }
        
        const { error: redirectError } = await stripe.redirectToCheckout({
          sessionId: data.sessionId
        });

        if (redirectError) {
          console.error('Redirect to checkout failed:', redirectError);
          throw new Error(redirectError.message || "Unable to redirect to payment page");
        }
      } catch (error: any) {
        console.error('Payment session error:', error);
        
        // Enhanced error handling with immediate cleanup
        if (confirmedBookingId) {
          console.log("Cleaning up failed booking:", confirmedBookingId);
          try {
            await cleanupFailedBooking(confirmedBookingId);
            console.log("Successfully cleaned up failed booking");
          } catch (cleanupError) {
            console.error("Error during booking cleanup:", cleanupError);
          }
        }
        
        // Special handling for authentication errors
        if (error.message && (
          error.message.toLowerCase().includes("auth") || 
          error.message.toLowerCase().includes("session expired") ||
          error.message.toLowerCase().includes("log in again")
        )) {
          console.log("Authentication error detected, redirecting to login");
          
          toast({
            variant: "destructive",
            title: "Authentication Error",
            description: "Your session has expired. Please log in again to continue with payment.",
            duration: 10000,
          });
          
          setTimeout(() => {
            navigate('/client-auth', { 
              state: { 
                returnTo: '/payment',
                bookingError: "Session expired during payment. Please log in again and retry."
              } 
            });
          }, 1000);
        } else {
          // For non-auth errors, show user-friendly message
          toast({
            variant: "destructive",
            title: "Payment Failed",
            description: error.message || "There was an error processing your payment. Please try again."
          });
        }
        
        throw error;
      }
    } catch (error: any) {
      console.error('Payment process error:', error);
      // Error already handled in specific catches above
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handlePaymentSession
  };
};
